// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Button } from '@imprivata-cloud/components';
import { useQuery } from '@tanstack/react-query';
import type { MenuProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getTestTenantInfo } from '../api/portalServices';
import { withRole } from '../common/authorization/Roles';
import { useSession } from '../common/authorization/context/Session';
import UserMenu from '../components/dropdown/UserMenu';
import PageHeader from '../components/layout/PageHeader';
import { getIdpLookupUrl } from '../utils/utils';
import { CUSTOMER_EXPERIENCE_CENTER_URL, ROLE_TYPE_ADMIN } from './constants';

import SettingsIcon from '../assets/icons/settings.svg?react';

type LandingPageHeaderProps = {
  userLogout: () => void;
  handleSettings?: () => void;
};

export enum TenantType {
  PRIMARY = 'PRIMARY',
  TEST = 'TEST',
}

export enum SelectValues {
  USER_UPN = 'user-upn',
  IMPRIVATA_IDENTITY = 'imprivata-identity',
  LOGOUT = 'Logout',
}

const TRANSLATION_ROOT = 'portal.landing.header';

export const PortalHeader = ({ userLogout, handleSettings }: LandingPageHeaderProps) => {
  const { tenantId, tenantType, userInfo } = useSession();
  const { t } = useTranslation();
  const SettingsButton = withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Button);
  const [searchParams] = useSearchParams();
  const testTenantInfo = useQuery({
    queryKey: ['testTenantInfo', tenantId],
    queryFn: async () => await getTestTenantInfo(tenantId || ''),
    enabled: Boolean(tenantType === 'PRIMARY'),
    refetchOnWindowFocus: false,
  });

  const headerSelectOptions: MenuProps['items'] = useMemo(
    () => [
      {
        key: SelectValues.IMPRIVATA_IDENTITY,
        label: t(`${TRANSLATION_ROOT}.select.imprivata-identity`),
      },
      {
        key: SelectValues.LOGOUT,
        label: t(`${TRANSLATION_ROOT}.select.logout`),
      },
    ],
    [t],
  );

  const handleImprivataIdentity = () => {
    const params = new URLSearchParams(searchParams);
    window.open(`/self-service?${params}`, '_blank');
  };

  const redirectToTestTenant = () => {
    const testTenantId = testTenantInfo?.data?.domainTenants[0]?.tenantId;
    if (testTenantId) {
      const idpUrl = getIdpLookupUrl(true, testTenantId);
      window.open(`${idpUrl}`, '_blank');
    }
  };

  const handleSelectChange = (value: string) => {
    switch (value) {
      case SelectValues.IMPRIVATA_IDENTITY:
        handleImprivataIdentity();
        break;
      case SelectValues.LOGOUT:
        userLogout();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div className="portal-header">
        <PageHeader
          className="content"
          rightSlot={
            <>
              {tenantType === 'PRIMARY' &&
                testTenantInfo?.data?.domainTenants &&
                testTenantInfo.data.domainTenants.length > 0 && (
                  <Button type={'text'} onClick={redirectToTestTenant}>
                    Go to sandbox environment
                  </Button>
                )}
              {tenantType === 'TEST' && <p>Sandbox use only</p>}
              <SettingsButton
                icon={<SettingsIcon fill={'#2c3f57'} />}
                className={'settings-button'}
                onClick={handleSettings}
                aria-label={t('portal.settings.title')}
              />
              <div className="profile-menu">
                <UserMenu
                  menu={{
                    items: headerSelectOptions,
                    onClick: ({ key }) => handleSelectChange(key),
                  }}
                  userUpn={userInfo?.userDisplayName || userInfo?.userUpn}
                  aria-label={'user-menu'}
                />
              </div>
            </>
          }
        />
      </div>
      <div className="page-sub-header">
        <div className="content">
          <a href={CUSTOMER_EXPERIENCE_CENTER_URL} target={'_blank'} rel={'noreferrer'}>
            CUSTOMER EXPERIENCE CENTER
          </a>
        </div>
      </div>
    </>
  );
};
